/**
 * BarcodeConfirmDialog - Match confirmation for low-confidence barcode scans
 *
 * PLATFORM:F1.P9-10 Phase 3: Shows matched candidates with confidence scores.
 * User can confirm, reject, or switch to manual entry.
 */
import React, { useState } from 'react';
import { X, Check, AlertTriangle, Search, Loader2, Layers } from 'lucide-react';
import { confirmBarcodeScan } from '../../services/vaultApi';

function ConfidenceBadge({ confidence }) {
  let color = 'bg-red-500/20 text-red-400';
  if (confidence >= 80) color = 'bg-yellow-500/20 text-yellow-400';
  if (confidence >= 90) color = 'bg-green-500/20 text-green-400';

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {confidence}% match
    </span>
  );
}

export default function BarcodeConfirmDialog({
  isOpen,
  onClose,
  barcode,
  candidates = [],
  onConfirmed,
  onManualEntry,
}) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleConfirm = async (candidate) => {
    setConfirming(true);
    setError(null);

    try {
      const matchType = candidate.comic_id ? 'comic'
        : 'product';
      const matchId = candidate.comic_id || candidate.product_id;

      const result = await confirmBarcodeScan(barcode, matchType, matchId);
      onConfirmed?.(result);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to confirm match');
    } finally {
      setConfirming(false);
    }
  };

  const handleManualEntry = () => {
    onManualEntry?.(barcode);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      data-testid="barcode-confirm-dialog"
    >
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-yellow-400" />
            <h2 className="text-white font-semibold">Confirm Match</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-zinc-800 transition-colors"
            aria-label="Close"
            data-testid="barcode-confirm-close"
          >
            <X size={18} className="text-zinc-400" />
          </button>
        </div>

        {/* Barcode info */}
        <div className="px-4 py-3 bg-zinc-800/50">
          <p className="text-zinc-400 text-xs uppercase tracking-wider">Scanned Barcode</p>
          <p className="text-white font-mono text-sm mt-0.5">{barcode}</p>
        </div>

        {/* Candidates */}
        <div className="px-4 py-3 space-y-2 max-h-80 overflow-y-auto">
          {candidates.length === 0 ? (
            <div className="text-center py-6">
              <Layers size={32} className="mx-auto text-zinc-600 mb-2" />
              <p className="text-zinc-400 text-sm">No candidates found</p>
            </div>
          ) : (
            candidates.map((candidate, idx) => (
              <div
                key={idx}
                className="bg-zinc-800 rounded-xl p-3 flex gap-3"
                data-testid={`barcode-candidate-${idx}`}
              >
                {/* Cover thumbnail */}
                <div className="w-12 h-16 bg-zinc-700 rounded-lg overflow-hidden flex-shrink-0">
                  {candidate.cover_url ? (
                    <img
                      src={candidate.cover_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Layers size={16} className="text-zinc-500" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {candidate.title || 'Unknown Title'}
                  </p>
                  {candidate.issue_number && (
                    <p className="text-zinc-400 text-xs">#{candidate.issue_number}</p>
                  )}
                  {candidate.publisher && (
                    <p className="text-zinc-500 text-xs">{candidate.publisher}</p>
                  )}
                  {/* V-BARCODE: the badge is a MEASUREMENT. A candidate that arrived via the
                      text-search fallback carries `confidence: null` — it has no score, and
                      rendering "null% match" (or worse, "undefined% match" styled red) would be
                      a fabricated number on the exact surface where the user decides what enters
                      their vault. No number, no badge. */}
                  {typeof candidate.confidence === 'number' && (
                    <div className="mt-1">
                      <ConfidenceBadge confidence={candidate.confidence} />
                    </div>
                  )}
                </div>

                {/* Confirm button */}
                <button
                  onClick={() => handleConfirm(candidate)}
                  disabled={confirming}
                  className="self-center p-2 rounded-lg bg-[#D4A03C] hover:bg-[#E0B04C] text-black transition-colors disabled:opacity-50"
                  style={{ minWidth: '40px', minHeight: '40px' }}
                  aria-label={`Confirm ${candidate.title}`}
                  data-testid={`barcode-confirm-btn-${idx}`}
                >
                  {confirming ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Check size={16} />
                  )}
                </button>
              </div>
            ))
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="px-4 py-2">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Footer */}
        <div className="px-4 py-3 border-t border-zinc-800 flex gap-2">
          <button
            onClick={handleManualEntry}
            className="flex-1 flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl py-2.5 text-sm transition-colors"
            style={{ minHeight: '44px' }}
            data-testid="barcode-manual-entry"
          >
            <Search size={16} />
            Manual Entry
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 rounded-xl py-2.5 text-sm transition-colors"
            style={{ minHeight: '44px' }}
            data-testid="barcode-confirm-cancel"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
